<script lang="ts" setup>
import { Delete, Plus } from '@element-plus/icons-vue'
import { ElSelect } from 'element-plus'
import { ref } from 'vue'

import HelpOldIcon from '@/assets/icons/help-old.svg?component'
import InfoIcon from '@/assets/icons/info.svg?component'
import { defaultTrafficSourceGroup } from '@/lib/helpers/trafficSourceCosts.helpers'
import type { GroupedTrafficSourceCostType } from '@/types/trafficSourceCost'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

const props = defineProps<{
  id: string
  currencySymbol: string
  trafficSources: { source: number; name: string }[]
  advertiserUsesCpa: boolean
  recommendedPrice?: string
}>()
const selectors = ref()
const hasCommission = defineModel<boolean>('hasCommission')
const hasAdditionalPayment = defineModel<boolean>('hasAdditionalPayment')
const additionalPayment = defineModel<number>('additionalPayment')
const groupedTrafficSourceCosts = defineModel<GroupedTrafficSourceCostType[]>('groupedTrafficSourceCosts', { default: [] })

const addItem = () => {
  groupedTrafficSourceCosts.value.push({ ...defaultTrafficSourceGroup })
}
const remove = (item: GroupedTrafficSourceCostType) => {
  const index = groupedTrafficSourceCosts.value.indexOf(item)
  if (index !== -1) {
    groupedTrafficSourceCosts.value.splice(index, 1)
  }
}
const removeSourcesInOtherGroups = (currenIndex: number) => {
  if (groupedTrafficSourceCosts.value.length > 1) {
    const sourceIndexes = groupedTrafficSourceCosts.value[currenIndex]?.sourceIndexes
    groupedTrafficSourceCosts.value.forEach((item, index) => {
      if (index !== currenIndex) {
        item.sourceIndexes = item.sourceIndexes.filter((source) => !sourceIndexes.includes(source))
      }
    })
  }
}
const handleSelect = (item: GroupedTrafficSourceCostType, index: number) => {
  selectors.value[index].blur()
  removeSourcesInOtherGroups(index)
}
const handleAllSelect = (item: GroupedTrafficSourceCostType, index: number) => {
  item.sourceIndexes = item.sourceIndexes.length === props.trafficSources.length ? [] : props.trafficSources.map((item) => item.source)
  selectors.value[index].blur()
  removeSourcesInOtherGroups(index)
}

const cpcRules = [
  {
    validator: (_, value: string, callback: (e?: Error) => void) => {
      if (!value) {
        return callback()
      }

      const formattedValue = Number(value)
      if (isNaN(formattedValue)) {
        return callback(new Error('Field must be a valid number'))
      }
      if (formattedValue < 0) {
        return callback(new Error('Field must be a positive number'))
      }
      if (formattedValue >= 1000) {
        return callback(new Error('Field must be less than 1000'))
      }

      return callback()
    },
    trigger: 'blur',
  },
]
const cpaRules = [
  {
    validator: (_, value: string, callback: (e?: Error) => void) => {
      if (!value) {
        return callback()
      }

      const formattedValue = Number(value)
      if (isNaN(formattedValue)) {
        return callback(new Error('Field must be a valid number'))
      }
      if (formattedValue < 0) {
        return callback(new Error('Field must be a positive number'))
      }
      if (formattedValue >= 100) {
        return callback(new Error('Field must be less than 100'))
      }

      return callback()
    },
    trigger: 'blur',
  },
]
</script>
<template>
  <BoxComponent :id="id" :mb="32" :mb-lg="48" :p="24" class="campaign-form__block">
    <BoxComponent :mb="32">
      <TypographyComponent id="campaign-payment-section" variant="headline-medium" color="#000000">Payment</TypographyComponent>
    </BoxComponent>
    <BoxComponent :mb="24">
      <FlexComponent justify="space-between" align="center">
        <TypographyComponent variant="title-large">Commission</TypographyComponent>
        <el-switch v-model="hasCommission" size="large" />
      </FlexComponent>
    </BoxComponent>
    <BoxComponent v-if="recommendedPrice" :mb="16" :p="16" class="campaign-payment-section__notification">
      <FlexComponent :gap="16">
        <el-icon :size="24">
          <InfoIcon />
        </el-icon>
        <TypographyComponent variant="body-large">
          We recommend a minimum commission of <b>{{ recommendedPrice }}/click</b>.
        </TypographyComponent>
      </FlexComponent>
    </BoxComponent>
    <template v-if="hasCommission">
      <BoxComponent v-for="(item, index) in groupedTrafficSourceCosts" :key="index" :mb="24" :p="16" class="campaign-form__commission-item">
        <FlexComponent :gap="8" align="center">
          <BoxComponent :mb="16" class="w-100">
            <BoxComponent :mb="8">
              <FlexComponent :gap="8" align="center">
                <TypographyComponent variant="title-large" style="margin-bottom: 0">Traffic source</TypographyComponent>
                <el-tooltip placement="bottom-start" effect="light" :show-arrow="false">
                  <el-icon :size="16">
                    <HelpOldIcon />
                  </el-icon>
                  <template #content>
                    <FlexComponent :gap="16" direction="column" style="max-width: 270px">
                      <div>
                        <TypographyComponent variant="label-large">Instagram</TypographyComponent>
                        <TypographyComponent variant="body-medium" color="#6f6f6f">
                          Traffic coming from native Instagram app.
                        </TypographyComponent>
                      </div>
                      <div>
                        <TypographyComponent variant="label-large">TikTok</TypographyComponent>
                        <TypographyComponent variant="body-medium">Traffic coming from native TikTok app.</TypographyComponent>
                      </div>
                      <div>
                        <TypographyComponent variant="label-large">Other</TypographyComponent>
                        <TypographyComponent variant="body-medium">
                          Traffic coming from any other source, e.g. websites (blogs, YouTube), messaging apps (WhatsApp, Telegram) and
                          manual link sharing.
                        </TypographyComponent>
                      </div>
                    </FlexComponent>
                  </template>
                </el-tooltip>
              </FlexComponent>
            </BoxComponent>
            <BoxComponent v-if="trafficSources.length" :mb="16">
              <ElSelect
                ref="selectors"
                v-model="item.sourceIndexes"
                multiple
                value-key="source"
                size="large"
                placeholder="Select source"
                :fit-input-width="true"
                no-data-text="No results found"
              >
                <el-option
                  v-for="sourceItem in trafficSources"
                  :key="sourceItem.source"
                  :label="sourceItem.name"
                  :value="sourceItem.source"
                  @click="handleSelect(item, index)"
                />
                <template #footer>
                  <TypographyComponent
                    variant="body-medium"
                    class="el-select-dropdown__item campaign-form__commission-select-all"
                    @click="handleAllSelect(item, index)"
                  >
                    {{ item.sourceIndexes.length < trafficSources.length ? 'Select all' : 'Unselect all' }}
                  </TypographyComponent>
                </template>
              </ElSelect>
            </BoxComponent>
            <BoxComponent>
              <FlexComponent :gap="16">
                <div class="w-100">
                  <TypographyComponent variant="title-large"> Cost per click </TypographyComponent>
                  <BoxComponent>
                    <el-form-item :prop="`groupedTrafficSourceCosts.${index}.rateCpc`" :rules="cpcRules">
                      <el-input v-model="item.rateCpc" size="large" @input="item.rateCpa = 0">
                        <template #suffix>
                          <TypographyComponent variant="title-large" style="margin-bottom: 0">
                            {{ currencySymbol }} / Click
                          </TypographyComponent>
                        </template>
                      </el-input>
                    </el-form-item>
                  </BoxComponent>
                </div>
                <div v-if="advertiserUsesCpa" class="w-100">
                  <TypographyComponent variant="title-large"> Cost per action </TypographyComponent>
                  <BoxComponent>
                    <el-form-item :prop="`groupedTrafficSourceCosts.${index}.rateCpa`" :rules="cpaRules">
                      <el-input v-model="item.rateCpa" size="large" type="number" step="any" @input="item.rateCpc = 0">
                        <template #suffix>
                          <TypographyComponent variant="title-large" style="margin-bottom: 0">% / Sale</TypographyComponent>
                        </template>
                      </el-input>
                    </el-form-item>
                  </BoxComponent>
                </div>
              </FlexComponent>
            </BoxComponent>
          </BoxComponent>
          <BoxComponent :p="8">
            <el-button link @click="remove(item)">
              <el-icon :size="24" color="#000000" style="opacity: 0.38">
                <Delete />
              </el-icon>
            </el-button>
          </BoxComponent>
        </FlexComponent>
      </BoxComponent>
      <BoxComponent v-if="groupedTrafficSourceCosts.length < trafficSources.length" :mb="32">
        <el-button round @click="addItem">
          <FlexComponent align="center" :gap="8">
            <el-icon :size="16">
              <Plus />
            </el-icon>
            Add another source
          </FlexComponent>
        </el-button>
      </BoxComponent>
    </template>
    <BoxComponent>
      <FlexComponent justify="space-between" align="center">
        <TypographyComponent variant="title-large"> Additional payment </TypographyComponent>
        <el-switch v-model="hasAdditionalPayment" size="large" />
      </FlexComponent>
    </BoxComponent>
    <BoxComponent v-if="hasAdditionalPayment" :mt="24" :mb="4">
      <el-form-item prop="additionalPayment">
        <el-input v-model.number="additionalPayment" size="large" type="number" step="any">
          <template #suffix>
            <TypographyComponent variant="title-large" style="margin-bottom: 0">{{ currencySymbol }}</TypographyComponent>
          </template>
        </el-input>
      </el-form-item>
    </BoxComponent>
    <TypographyComponent variant="body-small">
      The fixed fee applies to every accepted creator. You can later adjust the fixed fee for individual creators in the Manage Board.
    </TypographyComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
.text-color {
  color: var(--primary-color);
}

:deep(.el-tag.el-tag--info) {
  --el-tag-text-color: #000000;
  --el-tag-bg-color: #e0e4ea;
  --el-tag-border-color: #e0e4ea;
  --el-tag-hover-color: #000000;
}

:deep(.el-tag) {
  font-size: 14px;
  font-weight: 500;
  padding: 4px 8px;
  vertical-align: center;
}

.campaign-form__commission-item {
  border-radius: 16px;
  border: 1px solid rgba(3, 30, 81, 0.12);
}
.campaign-form__commission-select-all {
  line-height: 34px;
  font-size: 16px;
  font-weight: 500;
  padding: 0 20px;
  margin-inline: -10px;
}

.campaign-payment-section__notification {
  border-radius: 16px;
  background: var(--black08);
}
</style>
